// Camada da Capa do Projeto (ficha de lançamento) — 7 blocos guardados em jsonb
// na tabela product_cards, mais os campos que variam por sabor em flavors.
import { supabase } from './supabase';
import type { AppRole } from './database.types';
import type { ProjectCategory } from '../shared/types';
import { cpvUnitario } from '../shared/premissas';

export type BlockKey =
  | 'identificacao'
  | 'estrategia'
  | 'vendas'
  | 'viabilidade'
  | 'preco'
  | 'concorrencia'
  | 'governanca';

export const BLOCK_ORDER: BlockKey[] = [
  'estrategia',
  'identificacao',
  'vendas',
  'viabilidade',
  'preco',
  'concorrencia',
  'governanca',
];

/** Blocos exibidos na capa (Governança fica oculta). */
export const VISIBLE_BLOCKS: BlockKey[] = BLOCK_ORDER.filter((b) => b !== 'governanca');

/**
 * Projetos de "Melhoria de produto" usam uma capa enxuta: apenas o Resumo
 * Estratégico (Objetivo estratégico + Outros comentários).
 */
export const MELHORIA_BLOCKS: BlockKey[] = ['estrategia'];

/** Blocos aplicáveis à capa conforme a categoria do projeto. */
export function capaBlocks(category?: ProjectCategory): BlockKey[] {
  return category === 'melhoria' ? MELHORIA_BLOCKS : VISIBLE_BLOCKS;
}

export const BLOCK_TITLES: Record<BlockKey, string> = {
  identificacao: 'Identificação',
  estrategia: 'Resumo Estratégico',
  vendas: 'Projeção de Vendas',
  viabilidade: 'Viabilidade',
  preco: 'Preço e Posicionamento',
  concorrencia: 'Concorrência',
  governanca: 'Governança',
};

/**
 * Áreas responsáveis por cada bloco (edição). admin/gestor editam tudo.
 * Regra da Trevo: Marketing e Comercial (vendas) editam TODA a capa; as demais
 * áreas participam dos blocos técnicos delas (P&D, Custos).
 */
export const BLOCK_AREAS: Record<BlockKey, string[]> = {
  identificacao: ['marketing', 'vendas', 'pd'],
  estrategia: ['marketing', 'vendas'],
  vendas: ['marketing', 'vendas'],
  viabilidade: ['marketing', 'vendas', 'custos'],
  preco: ['marketing', 'vendas', 'custos'],
  concorrencia: ['marketing', 'vendas'],
  governanca: [],
};

export interface Identificacao {
  categoria_linha?: string;
  tipo_embalagem?: string;
  gramatura?: string;
  un_caixa?: string;
  validade?: string;
}
export interface Estrategia {
  racional?: string;
  publico_alvo?: string;
  proposta_valor?: string;
  objetivo?: string; // "Objetivo estratégico"
  riscos?: string;
  outros_comentarios?: string; // usado na capa enxuta de "Melhoria de produto"
}
/** Projeção de vendas de um sabor/SKU (12 meses). */
export interface VendaSabor {
  flavorId: string;
  nome?: string; // rótulo do sabor no momento do preenchimento (referência)
  custo_kg?: number; // CPV R$/kg
  preco_un?: number; // preço de venda R$/un
  preco_kg?: number; // preço de venda R$/kg
  volume_mensal?: number[]; // 12 posições (kg)
}
export interface Vendas {
  preco_kg?: number; // legado/agregado (mantido p/ compatibilidade)
  volume_mensal?: number[]; // legado/agregado (12 posições, kg)
  premissas?: string;
  por_sabor?: VendaSabor[]; // NOVO: projeção por SKU/sabor
}
export interface Viabilidade {
  // Premissas obrigatórias (etapa 6 — Análise de Viabilidade Financeira): preenchidas por Custos/Marketing.
  cpv_kg?: number; // CPV estimado (R$/kg)
  cpv_un?: number; // CPV estimado (R$/un.)
  preco_venda_kg?: number; // PV estimado (R$/kg)
  preco_venda_un?: number; // PV estimado (R$/un.)
  pco_un?: number; // PCO: preço sugerido ao consumidor (R$/un.)
  margem_min?: number; // margem mínima exigida (%)
  mc_prevista?: number; // % — margem de contribuição prevista (editável; sobrepõe o cálculo)
}
export interface Preco {
  preco_industria?: number;
  pmz?: number;
  preco_promo?: string;
  // Produto de ancoragem (definido pelo Marketing): orienta o Comercial na precificação
  ancora_nome?: string; // item da concorrência
  ancora_preco?: number; // preço atual do âncora ao consumidor (R$/un.)
  ancora_elasticidade?: number; // % do preço do âncora (90 = 10% abaixo; 108 = 8% acima)
  ancora_gramatura?: string; // vazio = mesma do nosso produto
  ancora_base?: 'un' | 'kg'; // só importa se as gramaturas diferem
}
export interface Coleta {
  local?: string;
  data?: string;
  preco?: number;
}
export interface Competidor {
  nome?: string;
  produto?: string;
  coletas?: Coleta[];
}
export interface Concorrencia {
  competidores?: Competidor[];
}
export interface GovMeta {
  by?: string;
  area?: string;
  at?: string;
}
export type Governanca = Partial<Record<BlockKey, GovMeta>>;

export interface ProductCard {
  identificacao: Identificacao;
  estrategia: Estrategia;
  vendas: Vendas;
  viabilidade: Viabilidade;
  preco: Preco;
  concorrencia: Concorrencia;
  governanca: Governanca;
}

export interface FlavorRow {
  id: string;
  name: string;
  codigo_interno: string | null;
  codigo_sap: string | null;
  ean: string | null; // EAN-13 (unidade)
  dun: string | null; // DUN-14 (caixa)
}

export function emptyCard(): ProductCard {
  return {
    identificacao: {},
    estrategia: {},
    vendas: {},
    viabilidade: {},
    preco: {},
    concorrencia: { competidores: [] },
    governanca: {},
  };
}

export async function loadCard(
  productId: string,
): Promise<{ card: ProductCard; flavors: FlavorRow[] }> {
  const [{ data: cardRow }, { data: flavorRows }] = await Promise.all([
    supabase
      .from('product_cards')
      .select('identificacao, estrategia, vendas, viabilidade, preco, concorrencia, governanca')
      .eq('product_id', productId)
      .maybeSingle(),
    supabase
      .from('flavors')
      .select('id, name, codigo_interno, codigo_sap, ean, dun')
      .eq('product_id', productId)
      .order('name'),
  ]);
  const base = emptyCard();
  const card: ProductCard = { ...base, ...((cardRow as Partial<ProductCard>) ?? {}) };
  return { card, flavors: (flavorRows as FlavorRow[]) ?? [] };
}

/** Salva um bloco e carimba a governança (quem/área/quando). */
export async function saveBlock(
  productId: string,
  key: BlockKey,
  value: unknown,
  governanca: Governanca,
): Promise<{ error?: string }> {
  const patch: Record<string, unknown> = { [key]: value };
  if (key !== 'governanca') patch.governanca = governanca;
  const { error } = await supabase.from('product_cards').update(patch).eq('product_id', productId);
  return { error: error?.message };
}

export interface BlockVersion {
  id: string;
  block: BlockKey;
  value: unknown;
  byName: string | null;
  at: string;
}

/** Guarda um snapshot do bloco no histórico (versão daquela projeção). */
export async function addBlockHistory(
  orgId: string,
  productId: string,
  block: BlockKey,
  value: unknown,
  byId: string | null,
  byName: string | null,
): Promise<void> {
  await supabase.from('card_block_history').insert({
    org_id: orgId,
    product_id: productId,
    block,
    value,
    by_id: byId,
    by_name: byName,
  });
}

/** Carrega o histórico de versões de um bloco (mais recente primeiro). */
export async function loadBlockHistory(
  productId: string,
  block: BlockKey,
): Promise<BlockVersion[]> {
  const { data } = await supabase
    .from('card_block_history')
    .select('id, block, value, by_name, at')
    .eq('product_id', productId)
    .eq('block', block)
    .order('at', { ascending: false });
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.id as string,
    block: r.block as BlockKey,
    value: r.value,
    byName: (r.by_name as string) ?? null,
    at: r.at as string,
  }));
}

export async function saveFlavor(
  flavorId: string,
  patch: Partial<Pick<FlavorRow, 'codigo_interno' | 'codigo_sap' | 'ean' | 'dun'>>,
): Promise<{ error?: string }> {
  const { error } = await supabase.from('flavors').update(patch).eq('id', flavorId);
  return { error: error?.message };
}

/** Fração de preenchimento da capa (blocos completos / total, sem governança). */
export function capaFraction(card: ProductCard, category?: ProjectCategory): number {
  const blocks = capaBlocks(category);
  const done = blocks.filter((b) => blockComplete(b, card, category)).length;
  return blocks.length ? done / blocks.length : 0;
}

/** Progresso de preenchimento da capa por produto (0..1), lido de product_cards. */
export async function loadCapaProgress(orgId: string): Promise<Map<string, number>> {
  const { data } = await supabase
    .from('product_cards')
    .select(
      'product_id, identificacao, estrategia, vendas, viabilidade, preco, concorrencia, governanca, products(category)',
    )
    .eq('org_id', orgId);
  const out = new Map<string, number>();
  for (const row of (data ?? []) as Record<string, unknown>[]) {
    const card: ProductCard = { ...emptyCard(), ...(row as Partial<ProductCard>) };
    const prod = row.products;
    const category = (Array.isArray(prod) ? prod[0]?.category : (prod as { category?: string })?.category) as
      | ProjectCategory
      | undefined;
    out.set(row.product_id as string, capaFraction(card, category));
  }
  return out;
}

/**
 * Áreas que colaboram em TODA a capa (todos os blocos), além de admin/gestor:
 * Marketing, Comercial (vendas), P&D, Custos e Regulatórios.
 */
export const FULL_CAPA_AREAS = ['marketing', 'vendas', 'pd', 'custos', 'regulatorios'];

export function canEditBlock(role: AppRole, areaKeys: string[], block: BlockKey): boolean {
  if (role === 'admin' || role === 'gestor') return true;
  if (role === 'editor') {
    if (areaKeys.some((a) => FULL_CAPA_AREAS.includes(a))) return true;
    return BLOCK_AREAS[block].some((a) => areaKeys.includes(a));
  }
  return false;
}

export function blockComplete(
  key: BlockKey,
  card: ProductCard,
  category?: ProjectCategory,
): boolean {
  // Capa enxuta de "Melhoria": só o Resumo Estratégico conta, e basta o objetivo.
  if (category === 'melhoria') {
    if (key === 'estrategia') return Boolean(card.estrategia.objetivo?.trim());
    return true;
  }
  switch (key) {
    case 'identificacao': {
      const i = card.identificacao;
      return Boolean(i.categoria_linha && i.tipo_embalagem && i.gramatura);
    }
    case 'estrategia': {
      const e = card.estrategia;
      return Boolean(e.racional && e.publico_alvo && e.proposta_valor);
    }
    case 'vendas': {
      const v = card.vendas;
      const legado = (v.preco_kg ?? 0) > 0 && (v.volume_mensal ?? []).some((x) => (x ?? 0) > 0);
      const porSabor = (v.por_sabor ?? []).some(
        (s) => (s.preco_kg ?? 0) > 0 && (s.volume_mensal ?? []).some((x) => (x ?? 0) > 0),
      );
      return Boolean(legado || porSabor);
    }
    case 'viabilidade': {
      const v = card.viabilidade;
      return Boolean((v.cpv_kg ?? 0) > 0 && (v.preco_venda_kg ?? 0) > 0 && (v.margem_min ?? 0) > 0);
    }
    case 'preco': {
      const p = card.preco;
      return Boolean((p.preco_industria ?? 0) > 0 && (p.pmz ?? 0) > 0);
    }
    case 'concorrencia':
      return (card.concorrencia.competidores ?? []).length > 0;
    case 'governanca':
      return BLOCK_ORDER.filter((b) => b !== 'governanca').every((b) => blockComplete(b, card));
    default:
      return false;
  }
}

/** Viabilidade com os campos derivados (CPV/un.) recalculados a partir do CPV/kg e da gramatura da Identificação. */
export function viabilidadeComDerivados(v: Viabilidade, gramatura: string | undefined): Viabilidade {
  return { ...v, cpv_un: cpvUnitario(v.cpv_kg, gramatura) };
}

/** Margem prevista (%) a partir de CPV e preço de venda por kg. */
export function margemPrevista(v: Viabilidade): number | null {
  if (!v.preco_venda_kg || v.preco_venda_kg <= 0 || v.cpv_kg == null) return null;
  return ((v.preco_venda_kg - v.cpv_kg) / v.preco_venda_kg) * 100;
}

// ---- Vendas por sabor: leitura, upsert e agregações --------------------------
export function vendaSaborFor(v: Vendas, flavorId: string): VendaSabor {
  return (v.por_sabor ?? []).find((s) => s.flavorId === flavorId) ?? { flavorId };
}

export function upsertVendaSabor(v: Vendas, row: VendaSabor): Vendas {
  const list = [...(v.por_sabor ?? [])];
  const i = list.findIndex((s) => s.flavorId === row.flavorId);
  if (i >= 0) list[i] = row;
  else list.push(row);
  return { ...v, por_sabor: list };
}

export interface VendasTotais {
  volume12m: number;
  receita12m: number;
  margemPonderada: number | null; // %
}

export function vendaVolume12m(s: VendaSabor): number {
  return (s.volume_mensal ?? []).reduce((a, b) => a + (b || 0), 0);
}

export function vendaMargem(s: VendaSabor): number | null {
  if (!s.preco_kg || s.preco_kg <= 0 || s.custo_kg == null) return null;
  return ((s.preco_kg - s.custo_kg) / s.preco_kg) * 100;
}

export function vendasTotais(v: Vendas): VendasTotais {
  const sabores = v.por_sabor ?? [];
  // Sem sabores: usa a projeção agregada legada (preço + volume mensal do card).
  if (sabores.length === 0) {
    const vol = (v.volume_mensal ?? []).reduce((a, b) => a + (b || 0), 0);
    return { volume12m: vol, receita12m: vol * (v.preco_kg ?? 0), margemPonderada: null };
  }
  let vol = 0,
    receita = 0,
    custoTot = 0;
  for (const s of sabores) {
    const sv = vendaVolume12m(s);
    vol += sv;
    receita += sv * (s.preco_kg ?? 0);
    custoTot += sv * (s.custo_kg ?? 0);
  }
  return {
    volume12m: vol,
    receita12m: receita,
    margemPonderada: receita > 0 ? ((receita - custoTot) / receita) * 100 : null,
  };
}

/** Volume mensal agregado (soma dos sabores), 12 posições. */
export function vendasVolumeMensalAgregado(v: Vendas): number[] {
  const sabores = v.por_sabor ?? [];
  // Sem sabores: cai no volume mensal agregado legado do card.
  if (sabores.length === 0) {
    const leg = v.volume_mensal ?? [];
    return Array.from({ length: 12 }, (_, i) => leg[i] || 0);
  }
  const acc = Array(12).fill(0) as number[];
  for (const s of sabores) {
    const vm = s.volume_mensal ?? [];
    for (let i = 0; i < 12; i++) acc[i] = (acc[i] ?? 0) + (vm[i] || 0);
  }
  return acc;
}

/** Preço médio ponderado (R$/kg) sobre os sabores. */
export function vendasPrecoMedioKg(v: Vendas): number {
  const t = vendasTotais(v);
  return t.volume12m > 0 ? t.receita12m / t.volume12m : 0;
}
